import { Component, TemplateFunction } from '@ribajs/core'
import { ShopifyCartService } from '../services/index.js'

import type {
  ShopifyProduct,
  ShopifyProductVariant,
  LineItemProperties,
} from '../types/index.js'

interface Scope {
  // OPTIONS
  product?: ShopifyProduct
  variant?: ShopifyProductVariant
  quantity: number
  /** Order quantity limit of the current product variant, if this is smaller than 0, the limit is disabled */
  orderQuantityLimit: number
  /** True if the minimum order quantity is not reached */
  quantityMinimumUndercut: boolean
  /** True if the maximum order quantity is exceeded */
  quantityLimitExceeded: boolean
  /** Current (main or sub) collection */
  collectionHandle?: string
  /** Defined if there is a parent / main collection, in this case `collectionHandle` is the sub collection */
  parentCollectionHandle?: string
  /** Set this to `true` if the `sb-card-product` is a product recommendation (used for tracking) */
  isRecommendation: boolean
  /** Set this to `true` if the `sb-card-product` is a customer favorite product */
  isFavorite: boolean
  /** Set this to `true` if the `sb-card-product` is a free present for the customer itself */
  isFreePresent: boolean
  /** If true a add-to-cart loading animation is shown */
  pending: boolean
  /** If true a add-to-cart process was success */
  success: boolean
  /** If true a add-to-cart process was failed */
  failed: boolean

  // METHODS
  addToCart: SbAddToCartComponent['addToCart']
  decrease: SbAddToCartComponent['decrease']
  increase: SbAddToCartComponent['increase']

  // TEMPLATES
}

export class SbAddToCartComponent extends Component {
  public static tagName = 'sb-add-to-cart'

  lifecycleOptions = {
    ignore: true,
  }

  public scope: Scope = {
    // OPTIONS
    product: undefined,
    variant: undefined,
    quantity: 1,
    orderQuantityLimit: -1,
    quantityLimitExceeded: false,
    quantityMinimumUndercut: false,
    collectionHandle: undefined,
    isRecommendation: false,
    isFavorite: false,
    isFreePresent: false,
    pending: false,
    success: false,
    failed: false,

    // METHODS
    addToCart: this.addToCart,
    decrease: this.decrease,
    increase: this.increase,

    // TEMPLATES
  }

  static get observedAttributes(): string[] {
    return [
      'product',
      'variant',
      'quantity',
      'order-quantity-limit',
      'collection-handle',
      'is-recommendation',
      'is-favorite',
    ]
  }

  protected requiredAttributes(): string[] {
    return ['product']
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbAddToCartComponent.observedAttributes)
    this.addEventListeners()
  }

  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  /**
   * Additional line item cart properties, used for tracking
   * @returns
   */
  protected getProperties() {
    const cartProperties: LineItemProperties = {}
    // Do not add the collection handle to the cart properties, because this results on a new line item for each collection
    // if (this.scope.collectionHandle) {
    //   cartProperties._collection = this.scope.collectionHandle
    // }
    if (this.scope.parentCollectionHandle) {
      cartProperties._parentCollection = this.scope.parentCollectionHandle
    }

    cartProperties._isRecommendation = this.scope.isRecommendation
    cartProperties._isFavorite = this.scope.isFavorite
    cartProperties._isFreePresent = this.scope.isFreePresent
    cartProperties._orderQuantityLimit = this.scope.orderQuantityLimit

    return cartProperties
  }

  /**
   * Add the selected variant (selected by the selected options) to the card
   * @returns
   */
  public async addToCart(event?: Event) {
    if (!this.scope.variant || !this.scope.product) {
      return
    }
    event?.preventDefault()
    try {
      this.onCartRequestStart()
      await ShopifyCartService.add(
        this.scope.product,
        this.scope.variant.id,
        this.scope.quantity,
        this.getProperties(),
      )
    } catch (error) {
      this.onCartRequestFailed()
      console.error('addToCart error', error)
    }
  }

  /**
   * Increase the product quantity the user can add to the card
   */
  public increase() {
    this.scope.quantity++
    this.validateQuantity()
  }

  /**
   * Decrease the product quantity the user can add to the card
   */
  public decrease() {
    this.scope.quantity--
    this.validateQuantity()
  }

  public validateQuantity() {
    if (this.scope.quantity <= 0) {
      this.scope.quantity = 1
      this.scope.quantityMinimumUndercut = true
    } else {
      this.scope.quantityMinimumUndercut = false
    }
    if (!this.scope.variant?.id) {
      console.error("Can't validate quantity without a selected variant")
      return
    }
    const currentCartItemQuantity =
      ShopifyCartService.getLineItem(this.scope.variant?.id)?.quantity || 0
    const validateRes = ShopifyCartService.validateQuantity(
      this.scope.quantity,
      currentCartItemQuantity,
      this.scope.orderQuantityLimit,
    )
    this.scope.quantity = validateRes.quantity
    this.scope.quantityLimitExceeded = validateRes.exceeded || false
  }

  protected onCartRequestStart() {
    this.scope.pending = true
  }

  protected onCartRequestComplete() {
    if (this.scope.pending) {
      this.scope.pending = false
      this.scope.success = !this.scope.failed
      setTimeout(() => {
        this.scope.success = false
        this.scope.failed = false
      }, 2000)
    }
  }

  protected onCartRequestFailed() {
    this.scope.pending = false
    this.scope.success = false
    this.scope.failed = true
    setTimeout(() => {
      this.scope.success = false
      this.scope.failed = false
    }, 2000)
  }

  async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbAddToCartComponent.tagName}-ready`)
  }

  protected addEventListeners() {
    ShopifyCartService.events.on(
      'ShopifyCart:request:complete',
      this.onCartRequestComplete,
      this,
    )
  }

  protected removeEventListeners() {
    ShopifyCartService.events.off(
      'ShopifyCart:request:complete',
      this.onCartRequestComplete,
      this,
    )
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
